import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css'
import { CssBaseline, ThemeProvider, CircularProgress, Box } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authstore';
import { useShallow } from 'zustand/react/shallow';
import PrivateRoute from './router/privateRouters';
import DashboardLayout from './layouts/DashboardLayout';
import customTheme from './theme/theme';
import AuthLayout from './layouts/AuthLayout';
import { ROUTES } from './router/routes';
import ErrorBoundary from './components/ErrorBoundary';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DrawerProvider } from './services/providers/DrawerContext';
import { Suspense, lazy, useMemo } from 'react';
import { useInstituteStore } from './store/institutestore';
import { usePrefetchRoutes } from './hooks/usePrefetchRoutes';
import ErrorPanel from './components/ErrorPanel/ErrorPanel';
import MonitoringStatus from './components/DevTools/MonitoringStatus';
import { FeatureFlagRoute } from './components/FeatureFlagRoute';
import { PrintTitleBinder } from './components/PrintSchoolHeader';

// Eagerly loaded — critical path (auth flow, errors, public pages)
import { Home } from './pages/Home';
import TransformationPage from './pages/Home/TransformationPage';
import WhySkolarMindsPage from './pages/Home/WhySkolarMindsPage';
import BookDemoPage from './pages/BookDemo';
// IntelligencePage removed — archived at public/intelligence.html
import LoginPage from './pages/Auth/LoginPage';
import SignupForm from './components/Signup';
import NotFound from './pages/Error/NotFound';
import Unauthorized from './pages/Error/Unauthorized';

// Legal pages
const TermsAndConditions = lazy(() => import('./pages/Legal/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/Legal/PrivacyPolicy'));

// Shared loading fallback
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress />
  </Box>
);

// Auth & User pages
const SelectProfile = lazy(() => import('./pages/Auth/SelectProfile'));
const SubscribePage = lazy(() => import('./pages/Auth/Subscribe'));
const VerifyPhone = lazy(() => import('./pages/Auth/VerifyPhone'));
const SetPin = lazy(() => import('./pages/Auth/SetPin'));
const SetPassword = lazy(() => import('./pages/Auth/SetPassword'));
const ForgotPin = lazy(() => import('./pages/Auth/ForgotPin'));
const ForgotPassword = lazy(() => import('./pages/User/forgotPassword'));
const ResetPassword = lazy(() => import('./pages/User/resetPassword'));
const VerifyEmail = lazy(() => import('./pages/User/VerifyEmail'));
const EmailNotVerifiedNotice = lazy(() => import('./pages/User/EmailNotVerifiedNotice'));
const ProfilePage = lazy(() => import('./pages/User/ProfilePage'));

// Dashboards
const AdminDashboard = lazy(() => import('./pages/Dashboard'));
const EmployeeDashboard = lazy(() => import('./pages/EmployeePortal/Dashboard'));
const StudentDashboard = lazy(() => import('./pages/StudentPortal/Dashboard'));

// Institute Settings
const InstituteProfile = lazy(() => import('./pages/Institute/Profile'));
const BoardTypesPage = lazy(() => import('./pages/Institute/Settings/BoardTypes'));
// Financial Years hidden for upcoming release — page preserved on disk
// const FinancialYearsPage = lazy(() => import('./pages/Institute/Settings/FinancialYears'));
const DocumentLogosPage = lazy(() => import('./pages/Institute/Settings/DocumentLogos'));

// Academic
const AcademicYearPage = lazy(() => import('./pages/AcademicYear'));
const ClassesPage = lazy(() => import('./pages/Classes'));
const ClassForm = lazy(() => import('./pages/Classes/ClassForm'));
const SubjectsList = lazy(() => import('./pages/Subjects'));
const ManageClassSubjects = lazy(() => import('./pages/Subjects/ClassSubjects'));
const AssignTeacher = lazy(() => import('./pages/Subjects/AssignTeacher'));
const CreateSubjectsForm = lazy(() => import('./pages/Subjects/CreateSubjectsForm'));
const EditAssignment = lazy(() => import('./pages/Subjects/EditAssignment'));
const GradingSystemList = lazy(() => import('./pages/GradingSystem'));
const GradingSystemForm = lazy(() => import('./pages/GradingSystem/GradingSystemForm'));
const GradingSystemView = lazy(() => import('./pages/GradingSystem/GradingSystemView'));
const EventsPage = lazy(() => import('./pages/Events'));
const EventDetailsPage = lazy(() => import('./pages/Events/EventDetailsPage'));
const HolidaysPage = lazy(() => import('./pages/Calender/HolidaysPage'));
const Settings = lazy(() => import('./pages/Settings'));

// Students & Staff
const StudentsPage = lazy(() => import('./pages/Students'));
const StudentSummaryPage = lazy(() => import('./pages/Students/StudentSummaryPage'));
const StudentForm = lazy(() => import('./pages/Students/StudentForm'));
const GenerateCertificatePage = lazy(() => import('./pages/Certificates/GenerateCertificatePage'));
const PendingOverrideApprovals = lazy(() => import('./pages/Certificates/PendingOverrideApprovals'));
const Employees = lazy(() => import('./pages/Employees').then(m => ({ default: m.Employees })));
const RoleWiseDirectoryPage = lazy(() => import('./pages/Employees/RoleWiseDirectoryPage'));
const EmployeeSummaryPage = lazy(() => import('./pages/Employees/EmployeeSummaryPage'));
const EmployeeForm = lazy(() => import('./pages/Employees/EmployeeForm').then(m => ({ default: m.EmployeeForm })));

// Attendance
const StudentAttendancePage = lazy(() => import('./pages/Attendance/StudentAttendancePage'));
const EmployeeAttendancePage = lazy(() => import('./pages/Attendance/EmployeeAttendancePage'));

// Finance
const FeeStructure = lazy(() => import('./pages/Fees/FeeStructure').then(m => ({ default: m.FeeStructure })));
const CollectFees = lazy(() => import('./pages/Fees/CollectFees'));
// Salary Components hidden for upcoming release — pages preserved on disk
// const SalaryComponents = lazy(() => import('./pages/SalaryComponents'));
// const ComputeSalary = lazy(() => import('./pages/SalaryComponents/ComputeSalary'));

// Leaves & Approvals
const LeavesPage = lazy(() => import('./pages/Leaves'));
const ApplyLeave = lazy(() => import('./pages/Leaves/ApplyLeave'));
const PendingApprovals = lazy(() => import('./pages/Leaves/PendingApprovals'));
const EmployeeLeavePolicyPage = lazy(() => import('./pages/Leaves/EmployeeLeavePolicy'));

// Exams
const ExamsPage = lazy(() => import('./pages/ExamManagement/ExamsPage'));
const CreateExamForm = lazy(() => import('./pages/ExamManagement/CreateExam'));
const ExamSchedulesPage = lazy(() => import('./pages/ExamManagement/ExamSchedulesPage'));
const BulkEditSchedulesPage = lazy(() => import('./pages/ExamManagement/EditSchedulePage'));
const EnterScoresPage = lazy(() => import('./pages/ExamManagement/EnterScoresPage'));
const MarksEntryPage = lazy(() => import('./pages/ExamManagement/MarksEntryPage'));
const ExamAttendancePage = lazy(() => import('./pages/ExamManagement/ExamAttendancePage'));
const ResultsView = lazy(() => import('./pages/ExamManagement/ExamResults').then(m => ({ default: m.ResultsView })));
const ResultDetailsView = lazy(() => import('./pages/ExamManagement/ResultDetailsView').then(m => ({ default: m.ResultDetailsView })));
const ExamScheduleView = lazy(() => import('./pages/ExamManagement/StudentExamScheduleView').then(m => ({ default: m.ExamScheduleView })));
const TeacherTodoPage = lazy(() => import('./pages/ExamManagement/TeacherTodoPage'));

// Reports
const ExamScoresReportBuilder = lazy(() => import('./pages/Reports/ExamScoresReportBuilder').then(m => ({ default: m.ExamScoresReportBuilder })));

// Timetable
const EnhancedDashboard = lazy(() => import('./pages/TimeTable/EnhancedDashboard'));
const ViewTimetables = lazy(() => import('./pages/TimeTable/ViewTimetables'));
const MasterGridView = lazy(() => import('./pages/TimeTable/MasterGridView'));
const WeekEditor = lazy(() => import('./pages/TimeTable/WeekEditor'));
const AutoGenerateWizard = lazy(() => import('./pages/TimeTable/AutoGenerateWizard'));
const TeacherScheduleView = lazy(() => import('./pages/TimeTable/TeacherScheduleView'));
const StudentWeekView = lazy(() => import('./pages/TimeTable/StudentWeekView'));
const ScheduleGroupManagement = lazy(() => import('./pages/TimeTable/ScheduleGroupManagement'));
const TeacherAvailability = lazy(() => import('./pages/TimeTable/TeacherAvailability'));
const BatchUpdateScreen = lazy(() => import('./pages/TimeTable/BatchUpdateScreen'));
const TimetableViewer = lazy(() => import('./pages/TimeTable/TimetableScheduleViewer'));

// Transport
const Vehicles = lazy(() => import('./pages/TransportManagement/Vehicles'));
const RoutesList = lazy(() => import('./pages/TransportManagement/RoutesList'));
const RouteStudentsList = lazy(() => import('./pages/TransportManagement/RouteStudentsList'));
const EnrollmentList = lazy(() => import('./pages/TransportManagement/EnrollmentList'));
const TransportAttendance = lazy(() => import('./pages/TransportManagement/TransportAttendance'));
const TransportLogsPage = lazy(() => import('./pages/TransportManagement/TransportLogsPage'));

// Hostel
const HostelList = lazy(() => import('./pages/HostelManagement/HostelList'));
const HostelForm = lazy(() => import('./pages/HostelManagement/HostelForm'));
const RoomManagement = lazy(() => import('./pages/HostelManagement/RoomManagement'));
const HostelDetails = lazy(() => import('./pages/HostelManagement/HostelDetails'));
const HostelResidents = lazy(() => import('./pages/HostelManagement/HostelResidents'));
const HostelAttendance = lazy(() => import('./pages/HostelManagement/HostelAttendance'));

// Communication
const CommunicationDashboard = lazy(() => import('./pages/Communication/Dashboard').then(m => ({ default: m.CommunicationDashboard })));
const FeeReminders = lazy(() => import('./pages/Communication/FeeReminders'));
const MessagingPage = lazy(() => import('./pages/Communication/MessagingPage'));
const NotificationsPage = lazy(() => import('./pages/Notifications/NotificationsPage'));

// Promotion
const ClassPromotionPage = lazy(() => import('./pages/Promotion/ClassPromotionPage'));
const SectionAssignmentPage = lazy(() => import('./pages/Promotion/SectionAssignmentPage'));
const SectionTransferPage = lazy(() => import('./pages/Promotion/SectionTransferPage'));

// Syllabus
const SyllabusPage = lazy(() => import('./pages/Syllabus'));
const SyllabusEditor = lazy(() => import('./pages/Syllabus/SyllabusEditor'));
const SyllabusDetail = lazy(() => import('./pages/Syllabus/SyllabusDetail'));
const StudentSyllabusPage = lazy(() => import('./pages/StudentPortal/Syllabus/StudentSyllabusPage'));

// Assignments
const AssignmentsPage = lazy(() => import('./pages/Assignments/AssignmentsPage'));
const CreateAssignmentPage = lazy(() => import('./pages/Assignments/CreateAssignmentPage'));
const AssignmentViewPage = lazy(() => import('./pages/Assignments/AssignmentViewPage'));
const AssignmentSubmissionsPage = lazy(() => import('./pages/Assignments/AssignmentSubmissionsPage'));
const StudentAssignmentsPage = lazy(() => import('./pages/StudentPortal/Assignments/StudentAssignmentsPage'));
const AssignmentAttemptPage = lazy(() => import('./pages/StudentPortal/Assignments/AssignmentAttemptPage'));
const AssignmentResultPage = lazy(() => import('./pages/StudentPortal/Assignments/AssignmentResultPage'));

// User Management & Roles
const UserManagementPage = lazy(() => import('./pages/UserManagement'));
const RolesPermissionsPage = lazy(() => import('./pages/RolesPermissions'));

// Online Classes – Teacher
const TeacherDashboardPage = lazy(() => import('./pages/OnlineClasses/TeacherDashboardPage'));
const TeacherSchedulePage = lazy(() => import('./pages/OnlineClasses/TeacherSchedulePage'));
const TeacherRecordingsPage = lazy(() => import('./pages/OnlineClasses/TeacherRecordingsPage'));
// Online Classes – Student
const StudentOnlineDashboardPage = lazy(() => import('./pages/StudentPortal/OnlineClasses/StudentDashboardPage'));
const StudentOnlineRecordingsPage = lazy(() => import('./pages/StudentPortal/OnlineClasses/StudentRecordingsPage'));
// Fees – Student
const StudentFeesPage = lazy(() => import('./pages/StudentPortal/Fees/StudentFeesPage'));
// Events – Student
const MyEventsPage = lazy(() => import('./pages/StudentPortal/Events/MyEventsPage'));
const MyEventDetailPage = lazy(() => import('./pages/StudentPortal/Events/MyEventDetailPage'));

// Homework
const HomeworkComingSoonPage = lazy(() => import('./pages/Homework/HomeworkComingSoonPage'));
const HomeworkEvaluationPage = lazy(() => import('./pages/Homework/HomeworkEvaluationPage'));

// Expenses
const ExpenseCategoriesPage = lazy(() => import('./pages/Expenses/Categories/ExpenseCategoriesPage'));
const MyClaimsPage = lazy(() => import('./pages/Expenses/Claims/MyClaimsPage'));
const SubmitClaimPage = lazy(() => import('./pages/Expenses/Claims/SubmitClaimPage'));
const ClaimDetailPage = lazy(() => import('./pages/Expenses/Claims/ClaimDetailPage'));
const PendingApprovalsPage = lazy(() => import('./pages/Expenses/Approvals/PendingApprovalsPage'));
const SettlementsPage = lazy(() => import('./pages/Expenses/Settlements/SettlementsPage'));

export default function App() {
  const { isAuthenticated, session, pendingProfileSelection, pendingPhoneVerification, pendingPasswordSetup } = useAuthStore(
    useShallow((s) => ({
      isAuthenticated: s.isAuthenticated,
      session: s.session,
      pendingProfileSelection: s.pendingProfileSelection,
      pendingPhoneVerification: s.pendingPhoneVerification,
      pendingPasswordSetup: s.pendingPasswordSetup,
    }))
  );
  const { branding } = useInstituteStore();

  usePrefetchRoutes();

  // Build tenant-branded theme (overrides primary color if institute has branding)
  const theme = useMemo(() => {
    if (!branding.primaryColor) return customTheme;
    return createTheme(customTheme, {
      palette: {
        primary: {
          main: branding.primaryColor,
        },
      },
    });
  }, [branding.primaryColor]);
  const getDashboardComponent = () => {
    if (session?.forcePasswordReset) {
      return <Navigate to={ROUTES.USER.RESET_PASSWORD} replace state={{ isFirstLogin: true, email: session.user.email, userId: session.user.id, token: null }} />;
    }
    if (session?.forcePinChange) {
      return <Navigate to={ROUTES.AUTH.SET_PIN} replace />;
    }
    if (!session?.emailConfirmed && session?.user?.roleType?.toLowerCase() !== 'student') {
      return <Navigate to={ROUTES.AUTH.EMAIL_NOT_VERIFIED} replace />;
    }
    // Gate unsubscribed students regardless of how they reached /dashboard
    if (session?.user?.roleType?.toLowerCase() === "student" && !session.user.isSubscribed) {
      return <Navigate to={ROUTES.AUTH.SUBSCRIBE} replace />;
    }

    switch (session?.user?.roleType?.toLowerCase()) {
      case "admin":
        return <AdminDashboard />;
      case "employee":
        return <EmployeeDashboard />;
      case "student":
        return <StudentDashboard />;
      default:
        return <Navigate to={ROUTES.AUTH.LOGIN} replace />;
    }
  };

  // Student timetable uses the new StudentWeekView
  const getStudentTimetableView = () => {
    if (session?.user?.roleType?.toLowerCase() === 'student') {
      return <StudentWeekView />;
    }
    return <Navigate to={ROUTES.AUTH.LOGIN} replace />;
  }
  const getSyllabusView = () => {
    if (session?.user?.roleType?.toLowerCase() === 'student') {
      return <StudentSyllabusPage />;
    }
    return <SyllabusPage />;
  }

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PrintTitleBinder />
        <DrawerProvider>
          <Toaster position="top-center" toastOptions={{ duration: 3000 }} />
          <ErrorPanel />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Router>
              <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route
                  path={ROUTES.PUBLIC.HOME}
                  element={
                    isAuthenticated ? (
                      <Navigate to={ROUTES.DASHBOARD.ROOT} replace />
                    ) : (
                      <Home />
                    )
                  }
                />
                 <Route path={ROUTES.PUBLIC.TRANSFORMATION} element={<TransformationPage />} />
                  <Route path={ROUTES.PUBLIC.WHY} element={<WhySkolarMindsPage />} />
                  <Route path={ROUTES.PUBLIC.BOOK_DEMO} element={<BookDemoPage />} />
                  {/* Intelligence route removed — archived at public/intelligence.html */}

                <Route
                  path={ROUTES.AUTH.LOGIN}
                  element={
                    isAuthenticated ? (
                      <Navigate to={ROUTES.DASHBOARD.ROOT} replace />
                    ) : (
                       <LoginPage />
                    )
                  }
                />
                <Route
                  path={ROUTES.AUTH.SELECT_PROFILE}
                  element={
                    isAuthenticated
                      ? <Navigate to={ROUTES.DASHBOARD.ROOT} replace />
                      : pendingProfileSelection
                        ? <SelectProfile />
                        : <Navigate to={ROUTES.AUTH.LOGIN} replace />
                  }
                />
                <Route
                  path={ROUTES.AUTH.VERIFY_PHONE}
                  element={
                    isAuthenticated
                      ? <Navigate to={ROUTES.DASHBOARD.ROOT} replace />
                      : pendingPhoneVerification
                        ? <VerifyPhone />
                        : <Navigate to={ROUTES.AUTH.LOGIN} replace />
                  }
                />

                <Route
                  path={ROUTES.AUTH.SUBSCRIBE}
                  element={
                    !isAuthenticated
                      ? <Navigate to={ROUTES.AUTH.LOGIN} replace />
                      : session?.user.isSubscribed
                        ? <Navigate to={ROUTES.DASHBOARD.ROOT} replace />
                        : <SubscribePage />
                  }
                />

                <Route path={ROUTES.AUTH.SET_PASSWORD} element={
                  isAuthenticated
                    ? <Navigate to={ROUTES.DASHBOARD.ROOT} replace />
                    :<AuthLayout><SetPassword /></AuthLayout>
                } />

                <Route path={ROUTES.AUTH.SET_PIN} element={
                  <AuthLayout>
                    <SetPin />
                  </AuthLayout>
                } />

                <Route path={ROUTES.AUTH.FORGOT_PIN} element={
                  <AuthLayout>
                    <ForgotPin />
                  </AuthLayout>
                } />

                <Route path={ROUTES.USER.FORGOT_PASSWORD} element={
                  <AuthLayout>
                    <ForgotPassword />
                  </AuthLayout>
                } />
                <Route path={ROUTES.USER.RESET_PASSWORD} element={
                  <AuthLayout>
                    <ResetPassword />
                  </AuthLayout>
                } />

                <Route path={ROUTES.AUTH.VERIFY_EMAIL} element={
                  <AuthLayout>
                    <VerifyEmail />
                  </AuthLayout>
                } />

                <Route path={ROUTES.AUTH.EMAIL_NOT_VERIFIED} element={
                  <AuthLayout>
                    <EmailNotVerifiedNotice />
                  </AuthLayout>
                } />


                <Route
                  path={ROUTES.AUTH.SIGNUP}
                  element={
                    <AuthLayout>
                      <SignupForm />
                    </AuthLayout>
                  }
                />

                {/* Legal Pages */}
                <Route path={ROUTES.PUBLIC.TERMS} element={<TermsAndConditions />} />
                <Route path={ROUTES.PUBLIC.PRIVACY} element={<PrivacyPolicy />} />

                <Route path="/" element={
                  !isAuthenticated
                    ? <Navigate to={ROUTES.AUTH.LOGIN} replace />
                    : session?.user?.roleType?.toLowerCase() === 'student' && !session.user.isSubscribed
                      ? <Navigate to={ROUTES.AUTH.SUBSCRIBE} replace />
                      : <DashboardLayout />
                }>
                  {/* Protected Dashboard Routes */}
                  <Route
                    path={ROUTES.DASHBOARD.ROOT}
                    element={
                      <PrivateRoute>
                        {getDashboardComponent()}
                      </PrivateRoute>
                    }
                  />

                  {/* Centralized Settings Page */}
                  <Route
                    path={ROUTES.SETTINGS.ROOT}
                    element={
                      <PrivateRoute requiredPermission="Permissions.InstitutePreferences.Read">
                        <Settings />
                      </PrivateRoute>
                    }
                  />

                  {/* Protected Admin Routes */}
                  <Route
                    path={ROUTES.DASHBOARD.INSTITUTE}
                    element={
                      <PrivateRoute requiredPermission="Permissions.InstitutePreferences.Read">
                        <InstituteProfile />
                      </PrivateRoute>
                    }
                  />

                  {/* Academic Year Settings */}
                  <Route
                    path={ROUTES.SETTINGS.ACADEMIC_YEARS.LIST}
                    element={
                      <PrivateRoute requiredPermission="Permissions.AcademicYears.Read">
                        <AcademicYearPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Board Types Settings Route */}
                  <Route
                    path={ROUTES.DASHBOARD.SETTINGS.BOARD_TYPES}
                    element={
                      <PrivateRoute requiredPermission="Permissions.InstitutePreferences.Read">
                        <BoardTypesPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Financial Years route hidden for upcoming release */}
                  {/* <Route
                    path={ROUTES.DASHBOARD.SETTINGS.FINANCIAL_YEARS}
                    element={
                      <PrivateRoute requiredPermission="Permissions.InstitutePreferences.Read">
                        <FinancialYearsPage />
                      </PrivateRoute>
                    }
                  /> */}

                  <Route
                    path={ROUTES.DASHBOARD.SETTINGS.DOCUMENT_LOGOS}
                    element={
                      <PrivateRoute requiredPermission="Permissions.InstitutePreferences.Read">
                        <DocumentLogosPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path={ROUTES.DASHBOARD.SETTINGS.DIGITAL_SIGNATURES}
                    element={<Navigate to={ROUTES.SETTINGS.ROOT} replace />}
                  />

                  <Route
                    path={ROUTES.DASHBOARD.SETTINGS.DOCUMENT_SIGNATURES}
                    element={<Navigate to={ROUTES.SETTINGS.ROOT} replace />}
                  />

                  {/* Protected Academic Routes */}
                  <Route
                    path={ROUTES.ACADEMIC.CLASSES.LIST}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Classes.Read">
                        <ClassesPage />
                      </PrivateRoute>
                    }
                  />
                  <Route path={ROUTES.ACADEMIC.CLASSES.NEW} element={
                    <PrivateRoute requiredPermission="Permissions.Classes.Create">

                      <ClassForm />

                    </PrivateRoute>} />
                  <Route path={ROUTES.ACADEMIC.CLASSES.EDIT} element={
                    <PrivateRoute requiredPermission="Permissions.Classes.Update">

                      <ClassForm />

                    </PrivateRoute>} />

                  {/* Protected Staff Routes */}
                  <Route
                    path={ROUTES.STAFF.EMPLOYEES.LIST}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Employees.Read">
                        <Employees />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.STAFF.EMPLOYEES.DIRECTORY}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Employees.Read">
                        <RoleWiseDirectoryPage />
                      </PrivateRoute>
                    }
                  />
                  <Route path={ROUTES.STAFF.EMPLOYEES.NEW} element={
                    <PrivateRoute requiredPermission="Permissions.Employees.Create">

                      <EmployeeForm />

                    </PrivateRoute>} />
                  <Route path={ROUTES.STAFF.EMPLOYEES.EDIT} element={
                    <PrivateRoute requiredPermission="Permissions.Employees.Update">

                      <EmployeeForm />

                    </PrivateRoute>} />
                  <Route path={ROUTES.STAFF.EMPLOYEES.SUMMARY} element={
                    <PrivateRoute requiredPermission="Permissions.Employees.Read">
                      <EmployeeSummaryPage />
                    </PrivateRoute>} />

                  {/* Protected Student Routes */}
                  <Route
                    path={ROUTES.STUDENT.LIST}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Students.Read">
                        <StudentsPage />
                      </PrivateRoute>
                    }
                  />
                  <Route path="students/new" element={
                    <PrivateRoute requiredPermission="Permissions.Students.Create">

                      <StudentForm />

                    </PrivateRoute>} />
                  <Route path={ROUTES.STUDENT.EDIT} element={
                    <PrivateRoute requiredPermission="Permissions.Students.Update">

                      <StudentForm />

                    </PrivateRoute>} />
                  <Route path={ROUTES.STUDENT.SUMMARY} element={
                    <PrivateRoute requiredPermission="Permissions.Students.Read">
                      <StudentSummaryPage />
                    </PrivateRoute>} />

                  <Route
                    path={ROUTES.CERTIFICATES.GENERATE}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Students.Read">
                        <GenerateCertificatePage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path={ROUTES.CERTIFICATES.TRANSFER_OVERRIDES}
                    element={
                      <PrivateRoute requiredPermission="Permissions.TransferCertificateClearance.ApproveOverride">
                        <PendingOverrideApprovals />
                      </PrivateRoute>
                    }
                  />

                  <Route path={ROUTES.FINANCE.FEE.STRUCTURE} element={
                    <PrivateRoute requiredPermission="Permissions.FeeStructure.Read">
                      <FeatureFlagRoute featureKey="fees">
                        <FeeStructure />
                      </FeatureFlagRoute>
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.FINANCE.FEE.COLLECT} element={
                    <PrivateRoute requiredPermission="Permissions.FeeStructure.Collect">
                      <FeatureFlagRoute featureKey="fees">
                        <CollectFees />
                      </FeatureFlagRoute>
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.ACADEMIC.SUBJECTS.LIST} element={
                    <PrivateRoute requiredPermission="Permissions.Subjects.Read">
                      <SubjectsList />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.ACADEMIC.SUBJECTS.MANAGE_CLASS_SUBJECTS} element={
                    <PrivateRoute requiredPermission="Permissions.Subjects.Read">

                      <ManageClassSubjects />

                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.ACADEMIC.SUBJECTS.NEW} element={
                    <PrivateRoute requiredPermission="Permissions.Subjects.Create">

                      <CreateSubjectsForm />

                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.ACADEMIC.SUBJECTS.ASSIGN_TEACHER} element={
                    <PrivateRoute requiredPermission="Permissions.Subjects.Update">
                      <AssignTeacher />
                    </PrivateRoute>
                  } />

                  <Route path={ROUTES.ACADEMIC.SUBJECTS.EDIT_ASSIGNMENT} element={
                    <PrivateRoute requiredPermission="Permissions.Subjects.Update">
                      <EditAssignment />
                    </PrivateRoute>
                  } />

                  {/* Salary Components route hidden for upcoming release */}
                  {/* <Route path={ROUTES.FINANCE.SALARY.COMPONENTS} element={
                    <PrivateRoute requiredPermission="Permissions.SalaryComponents.Read">
                      <SalaryComponents />
                    </PrivateRoute>
                  } /> */}

                  <Route path={ROUTES.ACADEMIC.GRADING.LIST} element={
                    <PrivateRoute requiredPermission="Permissions.GradeStructure.Read">

                      <GradingSystemList />

                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.ACADEMIC.GRADING.VIEW} element={
                    <PrivateRoute requiredPermission="Permissions.GradeStructure.Read">

                      <GradingSystemView />

                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.ACADEMIC.GRADING.CREATE} element={
                    <PrivateRoute requiredPermission="Permissions.GradeStructure.Create">

                      <GradingSystemForm />

                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.ACADEMIC.GRADING.EDIT} element={
                    <PrivateRoute requiredPermission="Permissions.GradeStructure.Update">

                      <GradingSystemForm />

                    </PrivateRoute>
                  } />
                  {/* Timetable Routes - Consolidated New Screens */}
                  <Route path={ROUTES.SCHEDULE.TIMETABLE.DASHBOARD} element={
                    <PrivateRoute requiredPermission='Permissions.TimeTable.Read'>
                      <EnhancedDashboard />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.SCHEDULE.TIMETABLE.ENHANCED_DASHBOARD} element={
                    <PrivateRoute requiredPermission='Permissions.TimeTable.Read'>
                      <EnhancedDashboard />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.SCHEDULE.TIMETABLE.VIEW_TIMETABLES} element={
                    <PrivateRoute requiredPermission='Permissions.TimeTable.Read'>
                      <ViewTimetables />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.SCHEDULE.TIMETABLE.SCHEDULE_VIEWER} element={
                    <PrivateRoute requiredPermission='Permissions.TimeTable.Read'>
                      <TimetableViewer />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.SCHEDULE.TIMETABLE.MASTER_GRID} element={
                    <PrivateRoute requiredPermission='Permissions.TimeTable.Read'>
                      <MasterGridView />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.SCHEDULE.TIMETABLE.WEEK_EDITOR} element={
                    <PrivateRoute requiredPermission='Permissions.TimeTable.Manage'>
                      <WeekEditor />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.SCHEDULE.TIMETABLE.SCHEDULE_PROFILES} element={
                    <PrivateRoute requiredPermission='Permissions.TimeTable.Manage'>
                      <ScheduleGroupManagement />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.SCHEDULE.TIMETABLE.AUTO_GENERATE} element={
                    <PrivateRoute requiredPermission='Permissions.TimeTable.Manage'>
                      <AutoGenerateWizard />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.SCHEDULE.TIMETABLE.BATCH_UPDATE} element={
                    <PrivateRoute requiredPermission='Permissions.TimeTable.Manage'>
                      <BatchUpdateScreen />
                    </PrivateRoute>
                  } />
                  {/* Student timetable view */}
                  <Route path={ROUTES.SCHEDULE.TIMETABLE.MY_TIMETABLE} element={
                    <PrivateRoute requiredPermission='Permissions.TimeTable.Read'>
                      {getStudentTimetableView()}
                    </PrivateRoute>
                  } />
                  {/* Teacher schedule view */}
                  <Route path={ROUTES.SCHEDULE.TIMETABLE.MY_SCHEDULE} element={
                    <PrivateRoute roles={['Teacher']} requiredPermission='Permissions.TimeTable.Read'>
                      <TeacherScheduleView />
                    </PrivateRoute>
                  } />
                  {/* Teacher availability view */}
                  <Route path={ROUTES.SCHEDULE.TIMETABLE.MY_AVAILABILITY} element={
                    <PrivateRoute roles={['Teacher']} requiredPermission='Permissions.TimeTable.Read'>
                      <TeacherAvailability />
                    </PrivateRoute>
                  } />

                  <Route path={ROUTES.EXAM.MANAGE} element={
                    <PrivateRoute requiredPermission="Permissions.Exam.Read">
                      <ExamsPage />
                    </PrivateRoute>
                  } />

                  {/* Teacher exam view — mirrors MY_SCHEDULE pattern */}
                  <Route path={ROUTES.EXAM.MY_EXAMS} element={
                    <PrivateRoute roles={['Teacher']} requiredPermission='Permissions.Exam.Read'>
                      <ExamsPage />
                    </PrivateRoute>
                  } />

                  <Route path={ROUTES.EXAM.TODOS} element={
                    <PrivateRoute roles={['Teacher']} requiredPermission='Permissions.Exam.Read'>
                      <TeacherTodoPage />
                    </PrivateRoute>
                  } />

                  {/* Backward compatibility - redirect old LIST route to MANAGE */}
                  <Route path="/exams" element={
                    <PrivateRoute requiredPermission="Permissions.Exam.Read">
                      <ExamsPage />
                    </PrivateRoute>
                  } />

                  <Route path={ROUTES.EXAM.SCHEDULES.LIST} element={
                    <PrivateRoute requiredPermission="Permissions.Exam.Read">
                      <ExamSchedulesPage />
                    </PrivateRoute>
                  } />

                  <Route path={ROUTES.EXAM.SCHEDULES.EDIT} element={
                    <PrivateRoute requiredPermission="Permissions.Exam.Update">
                      <BulkEditSchedulesPage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.EXAM.ENTER_SCORES} element={
                    <PrivateRoute requiredPermission="Permissions.Exam.Update">
                      <EnterScoresPage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.EXAM.CREATE} element={
                    <PrivateRoute requiredPermission="Permissions.Exam.Create">

                      <CreateExamForm />

                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.EXAM.EDIT} element={
                    <PrivateRoute requiredPermission="Permissions.Exam.Update">

                      <CreateExamForm />

                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.EXAM.MARKSENTRY} element={
                    <PrivateRoute requiredPermission="Permissions.Exam.Update">
                      <MarksEntryPage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.EXAM.ATTENDANCE} element={
                    <PrivateRoute requiredPermission="Permissions.Exam.Update">
                      <ExamAttendancePage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.USER.PROFILE} element={
                    <PrivateRoute>

                      <ProfilePage />

                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.EXAM.RESULTS} element={
                    <PrivateRoute requiredPermission="Permissions.Exam.Read">
                      <ResultsView />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.EXAM.VIEW_RESULTS} element={
                    <PrivateRoute requiredPermission="Permissions.Exam.Read">
                      <ResultDetailsView />
                    </PrivateRoute>
                  } />

                  <Route path={ROUTES.EXAM.STUDENT_SCHEDULES} element={
                    <PrivateRoute requiredPermission="Permissions.Exam.Read">
                      <ExamScheduleView />
                    </PrivateRoute>
                  } />

                  <Route path={ROUTES.REPORTS.EXAM_SCORES} element={
                    <PrivateRoute requiredPermission="Permissions.Reports.Read">
                      <ExamScoresReportBuilder />
                    </PrivateRoute>
                  } />

                  <Route path={ROUTES.CALENDAR.HOLIDAYS} element={
                    <PrivateRoute requiredPermission="Permissions.Holidays.Read">
                      <HolidaysPage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.LEAVE.LIST} element={
                    <PrivateRoute requiredPermission="Permissions.Leaves.Read">
                      <LeavesPage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.LEAVE.APPLY} element={
                    <PrivateRoute requiredPermission="Permissions.Leaves.Apply">
                      <ApplyLeave />
                    </PrivateRoute>
                  } />

                  {/* Attendance Routes */}
                  <Route path={ROUTES.ACADEMIC.ATTENDANCE.STUDENT} element={
                    <PrivateRoute requiredPermission="Permissions.Attendance.ManageStudent">
                      <StudentAttendancePage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.ACADEMIC.ATTENDANCE.EMPLOYEE} element={
                    <PrivateRoute requiredPermission="Permissions.Attendance.ManageEmployee">
                      <EmployeeAttendancePage />
                    </PrivateRoute>
                  } />

                  {/* Approval Routes */}
                  <Route
                    path={ROUTES.APPROVALS.PENDING}
                    element={
                      <PrivateRoute requiredPermission='Permissions.Leaves.Approve'>
                        <PendingApprovals type='employee' />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.APPROVALS.STUDENTS}
                    element={
                      <PrivateRoute requiredPermission='Permissions.Leaves.ApproveStudents'>
                        <PendingApprovals type='student' />
                      </PrivateRoute>
                    }
                  />

                  {/* Employee Portal Routes */}
                  <Route path={ROUTES.EMPLOYEE.DASHBOARD} element={
                    <PrivateRoute>

                      <EmployeeDashboard />

                    </PrivateRoute>
                  } />
                  <Route
                    path={ROUTES.EMPLOYEE.MY_DETAILS}
                    element={
                      <PrivateRoute>
                        <EmployeeSummaryPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path={ROUTES.ACADEMIC.EVENTS.LIST}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Events.Read">
                        <EventsPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path={ROUTES.ACADEMIC.EVENTS.DETAIL}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Events.Read">
                        <EventDetailsPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Homework Route */}
                  <Route
                    path={ROUTES.HOMEWORK.ROOT}
                    element={
                      <PrivateRoute>
                        <HomeworkComingSoonPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Homework Evaluation Route */}
                  <Route
                    path={ROUTES.HOMEWORK.EVALUATE}
                    element={
                      <PrivateRoute>
                        <HomeworkEvaluationPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Communication Routes */}
                  <Route
                    path={ROUTES.COMMUNICATION.DASHBOARD}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Communication.Dashboard">
                        <CommunicationDashboard />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.COMMUNICATION.FEE_REMINDERS}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Communication.FeeReminders">
                        <FeatureFlagRoute featureKey="fees">
                          <FeeReminders />
                        </FeatureFlagRoute>
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path={ROUTES.COMMUNICATION.MESSAGES}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Communication.Chat">
                        <MessagingPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path={ROUTES.NOTIFICATIONS.LIST}
                    element={
                      <PrivateRoute>
                        <NotificationsPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Compute Salary route hidden for upcoming release */}
                  {/* <Route
                    path={ROUTES.FINANCE.SALARY.COMPUTE}
                    element={
                      <PrivateRoute requiredPermission='Permissions.EmployeeStructure.ComputeSalary'>
                        <ComputeSalary />
                      </PrivateRoute>
                    }
                  /> */}

                  <Route
                    path={ROUTES.LEAVE.EMPLOYEE_LEAVE_POLICY}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Leaves.Policy.Manage">
                        <EmployeeLeavePolicyPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path={ROUTES.TRANSPORT.VEHICLES.LIST}
                    element={
                      <PrivateRoute requiredPermission='Permissions.Vehicles.Read'>
                        <Vehicles />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.TRANSPORT.ROUTES.LIST}
                    element={
                      <PrivateRoute requiredPermission='Permissions.Routes.Read'>
                        <RoutesList />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.TRANSPORT.ROUTES.STUDENTS}
                    element={
                      <PrivateRoute requiredPermission='Permissions.Routes.Read'>
                        <RouteStudentsList />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.TRANSPORT.ENROLLMENT.LIST}
                    element={
                      <PrivateRoute requiredPermission='Permissions.TransportManagement.Enrollment'>
                        <EnrollmentList />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.TRANSPORT.ATTENDANCE.LIST}
                    element={
                      <PrivateRoute requiredPermission='Permissions.TransportManagement.Attendance'>
                        <TransportAttendance />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.TRANSPORT.LOGS}
                    element={
                      <PrivateRoute requiredPermission='Permissions.TransportManagement.Attendance'>
                        <TransportLogsPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.HOSTEL.LIST}
                    element={
                      <PrivateRoute requiredPermission='Permissions.HostelManagement.Read'>
                        <HostelList />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.HOSTEL.NEW}
                    element={
                      <PrivateRoute requiredPermission='Permissions.HostelManagement.Create'>
                        <HostelForm />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.HOSTEL.EDIT}
                    element={
                      <PrivateRoute requiredPermission='Permissions.HostelManagement.Update'>
                        <HostelForm />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.HOSTEL.VIEW}
                    element={
                      <PrivateRoute requiredPermission='Permissions.HostelManagement.Read'>
                        <HostelDetails />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.HOSTEL.ROOMS.VIEW}
                    element={
                      <PrivateRoute requiredPermission='Permissions.HostelManagement.Read'>
                        <RoomManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.HOSTEL.ROOMS.NEW}
                    element={
                      <PrivateRoute requiredPermission='Permissions.HostelManagement.Create'>
                        <RoomManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.HOSTEL.ROOMS.EDIT}
                    element={
                      <PrivateRoute requiredPermission='Permissions.HostelManagement.Update'>
                        <RoomManagement />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.HOSTEL.RESIDENTS.LIST}
                    element={
                      <PrivateRoute requiredPermission='Permissions.HostelManagement.Read'>
                        <HostelResidents />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path={ROUTES.HOSTEL.ATTENDANCE.LIST}
                    element={
                      <PrivateRoute requiredPermission='Permissions.HostelManagement.Attendance'>
                        <HostelAttendance />
                      </PrivateRoute>
                    }
                  />

                  {/* Promotion Routes */}
                  <Route
                    path={ROUTES.PROMOTION.EXECUTE}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Students.Update">
                        <ClassPromotionPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.PROMOTION.SECTION_ASSIGNMENT}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Students.Update">
                        <SectionAssignmentPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.PROMOTION.SECTION_TRANSFER}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Students.Update">
                        <SectionTransferPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Syllabus Routes */}
                  <Route
                    path={ROUTES.SYLLABUS.LIST}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Syllabus.Read">
                        { getSyllabusView() }
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SYLLABUS.NEW}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Syllabus.Create">
                        <SyllabusEditor />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SYLLABUS.EDIT}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Syllabus.Update">
                        <SyllabusEditor isEdit />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.SYLLABUS.DETAIL}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Syllabus.Read">
                        <SyllabusDetail />
                      </PrivateRoute>
                    }
                  />

                  {/* User Management Routes */}
                  <Route
                    path={ROUTES.USER_MANAGEMENT.USERS}
                    element={
                      <PrivateRoute requiredPermission="Permissions.UserManagement.Read">
                        <UserManagementPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path={ROUTES.USER_MANAGEMENT.ROLES}
                    element={
                      <PrivateRoute requiredPermission="Permissions.Roles.Read">
                        <RolesPermissionsPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Assignment Routes – Teacher/Admin/Principal */}
                  <Route path={ROUTES.ASSIGNMENT.LIST} element={
                    <PrivateRoute requiredPermission="Permissions.Assignments.Read">
                      { (session?.user?.roleType?.toLowerCase() === 'student') ? <StudentAssignmentsPage /> : <AssignmentsPage /> }
                    </PrivateRoute>} />
                  <Route path={ROUTES.ASSIGNMENT.CREATE} element={<PrivateRoute requiredPermission="Permissions.Assignments.Create"><CreateAssignmentPage /></PrivateRoute>} />
                  <Route path={ROUTES.ASSIGNMENT.VIEW} element={<PrivateRoute requiredPermission="Permissions.Assignments.Read"><AssignmentViewPage /></PrivateRoute>} />
                  <Route path={ROUTES.ASSIGNMENT.EDIT} element={<PrivateRoute requiredPermission="Permissions.Assignments.Update"><CreateAssignmentPage /></PrivateRoute>} />
                  <Route path={ROUTES.ASSIGNMENT.SUBMISSIONS} element={<PrivateRoute requiredPermission="Permissions.Assignments.Read"><AssignmentSubmissionsPage /></PrivateRoute>} />

                  {/* Assignment Routes – Student */}
                  <Route path={ROUTES.ASSIGNMENT.ATTEMPT} element={<PrivateRoute requiredPermission="Permissions.Assignments.Submit"><AssignmentAttemptPage /></PrivateRoute>} />
                  <Route path={ROUTES.ASSIGNMENT.RESULT} element={<PrivateRoute requiredPermission="Permissions.Assignments.Read"><AssignmentResultPage /></PrivateRoute>} />

                  {/* Online Classes – Teacher */}
                  <Route path={ROUTES.ONLINE_CLASSES.TEACHER.ROOT} element={
                    <PrivateRoute roles={['Teacher']}>
                      <TeacherDashboardPage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.ONLINE_CLASSES.TEACHER.DASHBOARD} element={
                    <PrivateRoute roles={['Teacher']}>
                      <TeacherDashboardPage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.ONLINE_CLASSES.TEACHER.SCHEDULE} element={
                    <PrivateRoute roles={['Teacher']}>
                      <TeacherSchedulePage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.ONLINE_CLASSES.TEACHER.RECORDINGS} element={
                    <PrivateRoute roles={['Teacher']}>
                      <TeacherRecordingsPage />
                    </PrivateRoute>
                  } />

                  {/* Online Classes – Student */}
                  <Route path={ROUTES.ONLINE_CLASSES.STUDENT.ROOT} element={
                    <PrivateRoute roles={['Student']}>
                      <StudentOnlineDashboardPage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.ONLINE_CLASSES.STUDENT.RECORDINGS} element={
                    <PrivateRoute roles={['Student']}>
                      <StudentOnlineRecordingsPage />
                    </PrivateRoute>
                  } />

                  {/* Fees – Student */}
                  <Route path={ROUTES.FINANCE.FEE.MY_FEES} element={
                    <PrivateRoute roles={['Student']}>
                      <StudentFeesPage />
                    </PrivateRoute>
                  } />

                  {/* Events – Student */}
                  <Route path={ROUTES.ACADEMIC.EVENTS.MY_EVENTS} element={
                    <PrivateRoute roles={['Student']}>
                      <MyEventsPage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.ACADEMIC.EVENTS.MY_EVENT_DETAIL} element={
                    <PrivateRoute roles={['Student']}>
                      <MyEventDetailPage />
                    </PrivateRoute>
                  } />

                  {/* Expenses – Categories (admin only) */}
                  <Route path={ROUTES.EXPENSES.CATEGORIES.LIST} element={
                    <PrivateRoute requiredPermission="Permissions.Expenses.ManageCategories">
                      <ExpenseCategoriesPage />
                    </PrivateRoute>
                  } />

                  {/* Expenses – Approvals */}
                  <Route path={ROUTES.EXPENSES.APPROVALS.PENDING} element={
                    <PrivateRoute>
                      <PendingApprovalsPage />
                    </PrivateRoute>
                  } />

                  {/* Expenses – Settlements */}
                  <Route path={ROUTES.EXPENSES.SETTLEMENTS.LIST} element={
                    <PrivateRoute requiredPermission="Permissions.Expenses.Settle">
                      <SettlementsPage />
                    </PrivateRoute>
                  } />

                  {/* Expenses – Claims */}
                  <Route path={ROUTES.EXPENSES.CLAIMS.MY} element={
                    <PrivateRoute requiredPermission="Permissions.Expenses.ViewOwn">
                      <MyClaimsPage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.EXPENSES.CLAIMS.SUBMIT} element={
                    <PrivateRoute requiredPermission="Permissions.Expenses.Create">
                      <SubmitClaimPage />
                    </PrivateRoute>
                  } />
                  <Route path={ROUTES.EXPENSES.CLAIMS.DETAIL} element={
                    <PrivateRoute requiredPermission="Permissions.Expenses.ViewOwn">
                      <ClaimDetailPage />
                    </PrivateRoute>
                  } />
                </Route>



                {/* Error Routes */}
                <Route path={ROUTES.ERROR.UNAUTHORIZED} element={<Unauthorized />} />
                <Route path={ROUTES.ERROR.NOT_FOUND} element={<NotFound />} />
              </Routes>
              </Suspense>
            </Router>
          </LocalizationProvider>
        </DrawerProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
